import React, { useState } from 'react'
import { Box, Skeleton, Tab, Tabs, Typography } from '@mui/material'
import { Doughnut, Line } from 'react-chartjs-2'
import { allZero, axes, baseOptions, comparisonLine, currentLine, htmlTooltip, niceScale } from '../RestaurantDashboard/chartKit'
import { C, SERIES, cardSx, tabular } from '../RestaurantDashboard/tokens'
import { LegendItem, cardSubSx, cardTitleSx, tabsSx, tooltipDelta } from '../RestaurantDashboard/SalesOverTime'
import { dayShort, dayWithWeekday } from '../RestaurantDashboard/format'
import Empty from '../RestaurantDashboard/Empty'
import { $, count, NOT_TRACKED } from './saUtil'

const METRICS = [
  { key: 'gmv', label: 'GMV', money: true },
  { key: 'netRevenue', label: 'Net revenue', money: true },
  { key: 'commission', label: 'Commission', money: true },
  { key: 'orders', label: 'Orders', money: false },
  { key: 'aov', label: 'AOV', money: true }
]

const MONTH = new Intl.DateTimeFormat('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' })
const bucketLabel = (date, g) => (g === 'month' ? MONTH.format(new Date(`${date}T12:00:00Z`)) : g === 'week' ? `Wk of ${dayShort(date)}` : dayShort(date))

export function RevenueOverTime({ data, loading }) {
  const [metric, setMetric] = useState('gmv')
  const m = METRICS.find(x => x.key === metric)
  const series = data?.series || []
  const prev = data?.prevSeries || []
  const hasPrev = prev.length > 0
  const g = data?.granularity || 'day'
  // Charts take dollars; the API sends cents.
  const val = p => (p == null ? null : m.money ? Number(p[metric]) / 100 : Number(p[metric]))
  const cur = series.map(val)
  const cmp = hasPrev ? series.map((_, i) => val(prev[i])) : []
  const fmt = v => (m.money ? $(Math.round(v * 100)) : count(v))
  const scale = niceScale([cur, cmp], { money: m.money })

  return (
    <Box sx={{ ...cardSx, p: { xs: 2, md: 3 }, minWidth: 0 }}>
      <Typography component="h2" sx={cardTitleSx}>Revenue over time</Typography>
      <Typography sx={cardSubSx}>{g === 'day' ? 'Daily' : g === 'week' ? 'Weekly' : 'Monthly'} · dashboard orders</Typography>
      <Tabs value={metric} onChange={(_, v) => setMetric(v)} variant="scrollable" allowScrollButtonsMobile sx={{ ...tabsSx, mt: 1.5, borderBottom: `1px solid ${C.border}` }} aria-label="Metric">
        {METRICS.map(x => <Tab key={x.key} value={x.key} label={x.label} />)}
      </Tabs>
      <Box sx={{ display: 'flex', gap: 2, mt: 1.5 }} aria-hidden>
        <LegendItem color={C.brand} label="This period" />
        {hasPrev ? <LegendItem color={C.prev} dashed label="Comparison" /> : null}
      </Box>
      <Box sx={{ height: 280, mt: 1, position: 'relative' }} role="img" aria-label={`${m.label} over time`}>
        {loading && !data ? <Skeleton variant="rounded" height="100%" sx={{ borderRadius: '8px' }} /> : allZero(cur, cmp) ? <Empty compact message="Nothing to chart in this range." /> : (
          <Line
            data={{
              labels: series.map(p => bucketLabel(p.date, g)),
              datasets: [currentLine(cur, { fill: true, dots: series.length <= 45 }), ...(hasPrev ? [comparisonLine(cmp)] : [])]
            }}
            options={{
              ...baseOptions,
              scales: axes({ money: m.money, maxXTicks: 8, scale }),
              plugins: {
                ...baseOptions.plugins,
                tooltip: htmlTooltip(i => series[i] && ({
                  title: g === 'day' ? dayWithWeekday(series[i].date) : bucketLabel(series[i].date, g),
                  rows: [
                    { color: C.brand, label: 'This period', value: fmt(cur[i]) },
                    ...(hasPrev ? [{ color: C.prev, dashed: true, label: prev[i] ? bucketLabel(prev[i].date, g) : 'Comparison', value: cmp[i] == null ? '—' : fmt(cmp[i]) }] : [])
                  ],
                  delta: hasPrev && cmp[i] != null ? tooltipDelta(cur[i], cmp[i], fmt) : null
                }))
              }
            }}
          />
        )}
      </Box>
    </Box>
  )
}

const MIX_TABS = [
  { key: 'fulfillment', label: 'Fulfillment' },
  { key: 'channel', label: 'Channel' },
  { key: 'payment', label: 'Payment method' }
]

export function OrderMix({ data, loading }) {
  const [tab, setTab] = useState('fulfillment')
  const slices = data?.mix?.[tab] || []
  const total = slices.reduce((s, x) => s + x.count, 0)
  return (
    <Box sx={{ ...cardSx, p: { xs: 2, md: 3 }, minWidth: 0 }}>
      <Typography component="h2" sx={cardTitleSx}>Order mix</Typography>
      <Typography sx={cardSubSx}>Share of orders in this range</Typography>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" allowScrollButtonsMobile sx={{ ...tabsSx, mt: 1.5, borderBottom: `1px solid ${C.border}` }} aria-label="Order mix">
        {MIX_TABS.map(x => <Tab key={x.key} value={x.key} label={x.label} />)}
      </Tabs>
      {loading && !data ? <Skeleton variant="rounded" height={260} sx={{ mt: 2, borderRadius: '8px' }} /> : total === 0 ? (
        <Box sx={{ mt: 2 }}><Empty compact message="No orders in this range." /></Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center', gap: 2.5, mt: 2 }}>
          <Box sx={{ width: 170, height: 170, flexShrink: 0, position: 'relative' }} role="img"
            aria-label={slices.map(s => `${s.label} ${(s.share * 100).toFixed(1)}%`).join(', ')}>
            <Doughnut
              data={{ labels: slices.map(s => s.label), datasets: [{ data: slices.map(s => s.count), backgroundColor: slices.map((_, i) => SERIES[i % SERIES.length]), borderColor: '#fff', borderWidth: 2 }] }}
              options={{ responsive: true, maintainAspectRatio: false, cutout: '68%', plugins: { legend: { display: false }, tooltip: htmlTooltip(i => slices[i] && ({ title: slices[i].label, rows: [{ color: SERIES[i % SERIES.length], square: true, label: 'Orders', value: `${count(slices[i].count)} · ${(slices[i].share * 100).toFixed(1)}%` }], note: slices[i].gmv == null ? `GMV: ${NOT_TRACKED}` : `GMV ${$(slices[i].gmv)}` })) } }}
            />
            <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
              <Typography sx={{ ...tabular, fontSize: 22, fontWeight: 700, color: C.text, lineHeight: 1 }}>{count(total)}</Typography>
              <Typography sx={{ fontSize: 11.5, color: C.textMuted }}>orders</Typography>
            </Box>
          </Box>
          <Box component="table" sx={{ flex: 1, width: '100%', borderCollapse: 'collapse', fontSize: 13.5, ...tabular }}>
            <Box component="thead">
              <Box component="tr" sx={{ borderBottom: `1px solid ${C.border}` }}>
                {['', 'Orders', '%', 'GMV'].map((h, i) => <Box component="th" key={i} sx={{ textAlign: i === 0 ? 'left' : 'right', fontWeight: 500, color: C.textMuted, fontSize: 12, py: 0.75 }}>{h}</Box>)}
              </Box>
            </Box>
            <Box component="tbody">
              {slices.map((s, i) => (
                <Box component="tr" key={s.key} sx={{ borderBottom: i < slices.length - 1 ? `1px solid ${C.border}` : 0 }}>
                  <Box component="td" sx={{ py: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: SERIES[i % SERIES.length], flexShrink: 0 }} />
                      <Box component="span" sx={{ color: C.text }}>{s.label}</Box>
                    </Box>
                  </Box>
                  <Box component="td" sx={{ textAlign: 'right', color: C.text }}>{count(s.count)}</Box>
                  <Box component="td" sx={{ textAlign: 'right', color: C.textMuted }}>{(s.share * 100).toFixed(1)}%</Box>
                  <Box component="td" sx={{ textAlign: 'right', color: s.gmv == null ? C.textMuted : C.text }} title={s.gmv == null ? NOT_TRACKED : undefined}>{s.gmv == null ? '—' : $(s.gmv)}</Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  )
}
